import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  MaintenancePlan,
  MaintenancePlanList,
  MaintenancePlanUpdate,
} from '@/types/api';

/**
 * Hooks für Sprint-2-Wartungspläne (WT-204).
 *
 * Backend-Endpunkte (Source of Truth, app/api/routes/maintenance_plans.py):
 *   GET  /maintenance-plans?filter[customer_id]=…        -> Liste (Page)
 *   POST /customers/{id}/maintenance-plan/generate       -> regelbasiert (draft)
 *   PATCH /maintenance-plans/{plan_id}                    -> Item-/Status-Update
 *   POST /customers/{id}/maintenance-plan/approve         -> Freigabe (status=active)
 *
 * Hinweis: Generieren + Freigeben sind KUNDEN-scoped (1:1-Plan pro Kunde),
 * daher nehmen die Hooks eine customerId (nicht die planId).
 */

export function useCustomerMaintenancePlans(
  customerId: string | undefined
): ReturnType<typeof useQuery<MaintenancePlanList>> {
  return useQuery<MaintenancePlanList>({
    queryKey: queryKeys.maintenancePlans.byCustomer(customerId ?? ''),
    queryFn: () =>
      api.get<MaintenancePlanList>('/maintenance-plans', {
        'filter[customer_id]': customerId,
        limit: 50,
      }),
    enabled: Boolean(customerId),
  });
}

export interface GeneratePlanInput {
  customerId: string;
}

/**
 * Regelbasierte Generierung. Der Backend-Generator merged automatisch
 * Vertrags-Service-Profil UND System-/Service-Items (kein separater
 * fallback-Modus). Endpoint ist kunden-scoped, ohne Request-Body.
 */
export function useGeneratePlan(): ReturnType<
  typeof useMutation<MaintenancePlan, Error, GeneratePlanInput>
> {
  const qc = useQueryClient();
  return useMutation<MaintenancePlan, Error, GeneratePlanInput>({
    mutationFn: ({ customerId }) =>
      api.post<MaintenancePlan>(
        `/customers/${customerId}/maintenance-plan/generate`
      ),
    onSuccess: (plan) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.maintenancePlans.byCustomer(plan.customer_id),
      });
    },
  });
}

export interface UpdatePlanInput {
  planId: string;
  body: MaintenancePlanUpdate;
}

export function useUpdatePlan(): ReturnType<
  typeof useMutation<MaintenancePlan, Error, UpdatePlanInput>
> {
  const qc = useQueryClient();
  return useMutation<MaintenancePlan, Error, UpdatePlanInput>({
    mutationFn: ({ planId, body }) =>
      api.patch<MaintenancePlan, MaintenancePlanUpdate>(`/maintenance-plans/${planId}`, body),
    onSuccess: (plan) => {
      void qc.invalidateQueries({ queryKey: queryKeys.maintenancePlans.detail(plan.id) });
      void qc.invalidateQueries({
        queryKey: queryKeys.maintenancePlans.byCustomer(plan.customer_id),
      });
    },
  });
}

/**
 * Freigabe: setzt den Plan des Kunden auf status="active". Kunden-scoped
 * (POST /customers/{id}/maintenance-plan/approve), daher customerId als Arg.
 */
export function useActivatePlan(): ReturnType<typeof useMutation<MaintenancePlan, Error, string>> {
  const qc = useQueryClient();
  return useMutation<MaintenancePlan, Error, string>({
    mutationFn: (customerId) =>
      api.post<MaintenancePlan>(
        `/customers/${customerId}/maintenance-plan/approve`
      ),
    onSuccess: (plan) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.maintenancePlans.detail(plan.id),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.maintenancePlans.byCustomer(plan.customer_id),
      });
    },
  });
}
